import React, { useEffect, useMemo, useState } from "react";
import { FaGraduationCap, FaListUl, FaCheckCircle, FaBook, FaHistory, FaChartBar, FaTimesCircle, FaAward, FaArrowLeft } from "react-icons/fa";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import usePageTitle from "../hooks/usePageTitle";
import { authFetch } from "../utils/api";

/**
 * Education — Leo Portal design-system UI (Hash routing)
 * Routes:
 *   #/                → List page
 *   #/quiz/:quizId   → Quiz page
 */

const API_BASE = "https://dev.sliitleo.org/Backend";

/* -------- Utilities -------- */
function crc32(str) {
  let crc = 0 ^ (-1);
  for (let i = 0; i < str.length; i++) crc = (crc >>> 8) ^ CRC_TABLE[(crc ^ str.charCodeAt(i)) & 0xff];
  return (crc ^ (-1)) >>> 0;
}
const CRC_TABLE = (() => {
  let c, table = new Array(256);
  for (let n = 0; n < 256; n++) {
    c = n; for (let k = 0; k < 8; k++) c = ((c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1));
    table[n] = c >>> 0;
  }
  return table;
})();
const quizIdInt = (subject, quiz) => crc32(`${String(subject||"").trim()}||${String(quiz||"").trim()}`);

function useHashRoute() {
  const [hash, setHash] = useState(() => window.location.hash || "#/");
  useEffect(() => { const on = () => setHash(window.location.hash || "#/"); window.addEventListener("hashchange", on); return () => window.removeEventListener("hashchange", on); }, []);
  return hash;
}

/* ================================= Component ================================= */
export default function Education() {
  /* Page title */
  const hash = useHashRoute();
  const route = useMemo(() => {
    const m = hash.match(/^#\/quiz\/(\d+)/);
    if (m) return { name: "quiz", quizId: parseInt(m[1], 10) };
    return { name: "home" };
  }, [hash]);
  usePageTitle(route.name === "quiz" ? "Take Quiz" : "Education — Quizzes");

  /* State */
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [completedIds, setCompletedIds] = useState([]); // int[]
  const [quizScores, setQuizScores] = useState({});      // { [id:number]: number }
  const [totalScore, setTotalScore] = useState(null);

  const [userAnswers, setUserAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [quizStartTime, setQuizStartTime] = useState(null);
  const [quizEndTime, setQuizEndTime] = useState(null);

  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user")) || { name: "Student", role: "SLIIT Student", image: "/default-user.png" }; }
    catch { return { name: "Student", role: "SLIIT Student", image: "/default-user.png" }; }
  })();
  const sliitid = localStorage.getItem("sliitid") || "";

  /* Group questions → subject+quiz */
  const subjectQuizMap = {};
  questions.forEach(q => {
    const key = `${q.subject}||${q.quiz}`;
    if (!subjectQuizMap[key]) {
      const id = quizIdInt(q.subject, q.quiz);
      subjectQuizMap[key] = { subject: q.subject, quiz: q.quiz, quiz_id: id, questions: [], maxId: -1 };
    }
    subjectQuizMap[key].questions.push(q);
    const n = parseInt(q.id, 10);
    if (!isNaN(n) && n > subjectQuizMap[key].maxId) subjectQuizMap[key].maxId = n;
  });
  const subjectQuizList = Object.values(subjectQuizMap);
  const newUpdated = [...subjectQuizList].sort((a, b) => (b.maxId - a.maxId)).slice(0, 6);

  const totalQuizzes = subjectQuizList.length;
  const completedCount = subjectQuizList.filter(qz => completedIds.includes(qz.quiz_id)).length;
  const completionPct = totalQuizzes > 0 ? Math.round((completedCount / totalQuizzes) * 100) : 0;

  /* Load data */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true); setLoadErr("");
        const q = await fetch(`${API_BASE}/get_questions.php`);
        const qd = await q.json();
        if (!Array.isArray(qd?.questions)) throw new Error("Invalid questions format");
        if (!cancelled) setQuestions(qd.questions);

        if (sliitid) {
          const c = await fetch(`${API_BASE}/get_completed_quizzes.php`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sliitid })
          });
          const cd = await c.json();
          if (cd?.success) {
            if (!cancelled && Array.isArray(cd.completed_quiz_ids)) setCompletedIds(cd.completed_quiz_ids);
            if (!cancelled && cd.quiz_scores && typeof cd.quiz_scores === "object") setQuizScores(cd.quiz_scores);
            if (!cancelled && typeof cd.total_score === "number") setTotalScore(cd.total_score);
          }
        }
      } catch (e) {
        if (!cancelled) setLoadErr("Failed to load. Please try again.");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Routing helpers */
  const gotoHome = () => (window.location.hash = "#/");
  const gotoQuiz = (id) => (window.location.hash = `#/quiz/${id}`);

  /* Scoring */
  const calcScore = (qs, ans) => {
    if (!Array.isArray(qs) || !ans) return 0;
    return qs.reduce((acc, q) => {
      const correctIdx = parseInt(q.correct_option, 10) - 1;
      return acc + ((ans[q.id] === correctIdx) ? 10 : 0);
    }, 0);
  };
  const calcScoreWithTime = (qs, ans, timeTaken, free = 30, step = 60) => {
    const raw = calcScore(qs, ans);
    if (typeof timeTaken !== "number" || timeTaken <= free) return raw;
    const penalty = Math.floor((timeTaken - free) / step) * 10;
    return Math.max(0, raw - penalty);
  };

  /* Current quiz */
  const currentQuiz = useMemo(() => {
    if (route.name !== "quiz") return null;
    return subjectQuizList.find(q => q.quiz_id === route.quizId) || null;
  }, [route, subjectQuizList]);

  /* Reset on entering quiz */
  useEffect(() => {
    if (route.name === "quiz") {
      setUserAnswers({}); setShowResults(false); setQuizStartTime(Date.now()); setQuizEndTime(null);
    }
  }, [route.name]);

  return (
    <>
      <Sidebar />
      <Topbar user={user} />

      <main className="lg:ml-64 mt-14 w-full px-4 sm:px-6 md:px-8 py-6 md:py-10">
        {/* Scoped styles for quiz-option states only -- everything else uses the
            shared design system (panel/pill/tile/table/etc from shell.css). */}
        <style>{`
          .quiz-opt {
            display:flex; align-items:center; gap:10px; padding:10px 12px;
            background:var(--surface-2); border:1px solid var(--border); border-radius:var(--radius-sm);
            color:var(--text-1); font-weight:600; font-size:13px; cursor:pointer;
            transition:border-color .12s var(--ease), background-color .12s var(--ease);
          }
          .quiz-opt:hover { border-color: var(--border-strong); }
          .quiz-opt.is-active { background:var(--surface-3); border-color:var(--accent); }
          .quiz-opt.is-correct { border-color:var(--success); box-shadow: inset 0 0 0 1px var(--success); }
          .quiz-opt.is-wrong { border-color:var(--error); box-shadow: inset 0 0 0 1px var(--error); }
          .quiz-opt .bullet { width:14px; height:14px; border-radius:50%; border:2px solid var(--text-3); flex:0 0 auto; }
          .quiz-opt.is-active .bullet { border-color:var(--accent); background:var(--accent); }
          .quiz-opt.is-correct .bullet { border-color:var(--success); background:var(--success); }
          .quiz-opt.is-wrong .bullet { border-color:var(--error); background:var(--error); }
        `}</style>

        {route.name === "home" ? (
          <>
            <div className="page-head">
              <div>
                <h1><FaGraduationCap style={{ marginRight: 8, color: "var(--accent)" }} />Education · Quizzes</h1>
                <div className="page-desc">Sharpen your Leo knowledge — complete quizzes and track your progress.</div>
              </div>
            </div>

            {/* PRIORITY: completion ring + quick facts beside two supporting tiles */}
            <div className="priority">
              <div className="panel hero-metric">
                <div className="hero-top">
                  <div>
                    <div className="eyebrow"><FaCheckCircle style={{ marginRight: 6 }} />Quiz Progress</div>
                    <div className="hero-value">
                      {completedCount} <span style={{ fontSize: 16, fontWeight: 600, color: "var(--text-3)" }}>/ {totalQuizzes}</span>
                    </div>
                    <div className="hero-sub">Quizzes completed</div>
                  </div>
                  <div className="ring" style={{ "--pct": completionPct }} data-pct={`${completionPct}%`} />
                </div>
                <div className="hero-list">
                  <div className="hero-list-row">
                    <span className="name"><FaChartBar style={{ marginRight: 6, color: "var(--text-3)" }} />Total Score</span>
                    <span className="when num">{totalScore ?? "—"}</span>
                  </div>
                  <div className="hero-list-row">
                    <span className="name"><FaHistory style={{ marginRight: 6, color: "var(--text-3)" }} />Last Updated</span>
                    <span className="when num">{new Date().toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              <div className="panel tile">
                <div className="tile-label"><FaListUl style={{ marginRight: 6 }} />Total Quizzes</div>
                <div className="tile-value num">{totalQuizzes}</div>
              </div>

              <div className="panel tile">
                <div className="tile-label"><FaBook style={{ marginRight: 6 }} />In Progress</div>
                <div className="tile-value num">0</div>
              </div>
            </div>

            {/* New & Updated */}
            <div className="panel" style={{ marginBottom: "var(--sp-6)" }}>
              <div className="section-head"><h2>New &amp; Updated</h2></div>
              <div style={{ display: "flex", gap: 8, overflowX: "auto", padding: "0 var(--sp-4) var(--sp-4)" }}>
                {newUpdated.map(it => (
                  <span className="pill pill-success" key={`${it.subject}-${it.quiz}`} style={{ whiteSpace: "nowrap" }}>
                    <span className="dot" /> {it.subject}: {it.quiz}
                  </span>
                ))}
              </div>
            </div>

            {/* All Quizzes */}
            <div className="panel" style={{ marginBottom: "var(--sp-6)" }}>
              <div className="section-head">
                <h2>All Quizzes</h2>
                <span className="chip">{totalQuizzes} total</span>
              </div>
              {loading ? (
                <div className="empty-state">
                  <div className="spinner lg" />
                  <p>Loading quizzes…</p>
                </div>
              ) : loadErr ? (
                <div className="error-banner" style={{ margin: "0 var(--sp-4) var(--sp-4)" }}>
                  <FaTimesCircle /> {loadErr}
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Quiz</th>
                        <th>Questions</th>
                        <th>Est. Time</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {subjectQuizList.filter(sq => !completedIds.includes(sq.quiz_id)).length === 0 ? (
                        <tr>
                          <td colSpan={4}>
                            <div className="empty-state">
                              <div className="empty-icon"><FaCheckCircle /></div>
                              <h3>You're all caught up</h3>
                              <p>No quizzes waiting right now — check back soon for new ones.</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        subjectQuizList
                          .filter(sq => !completedIds.includes(sq.quiz_id))
                          .map((sq) => {
                            const qCount = sq.questions.length;
                            return (
                              <tr key={`${sq.subject}-${sq.quiz}`}>
                                <td>
                                  <div className="proj-name">{sq.subject}</div>
                                  <div className="proj-cat">{sq.quiz}</div>
                                </td>
                                <td className="num">{qCount} {qCount === 1 ? "Question" : "Questions"}</td>
                                <td>Est. 5–10 min</td>
                                <td className="row-actions">
                                  <button className="btn btn-primary btn-sm" onClick={() => gotoQuiz(sq.quiz_id)}>Start</button>
                                </td>
                              </tr>
                            );
                          })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Completed */}
            <div className="panel">
              <div className="section-head">
                <h2>Completed</h2>
                <span className="chip">{completedCount} done</span>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th>Quiz</th>
                      <th>Questions</th>
                      <th>Score</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subjectQuizList.filter(sq => completedIds.includes(sq.quiz_id)).length === 0 ? (
                      <tr>
                        <td colSpan={4}>
                          <div className="empty-state">
                            <div className="empty-icon"><FaBook /></div>
                            <h3>No completed quizzes yet</h3>
                            <p>Finish a quiz to see it show up here.</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      subjectQuizList
                        .filter(sq => completedIds.includes(sq.quiz_id))
                        .map(sq => {
                          const qCount = sq.questions.length;
                          const score = typeof quizScores[sq.quiz_id] === "number" ? quizScores[sq.quiz_id] : null;
                          return (
                            <tr key={`completed-${sq.subject}-${sq.quiz}`}>
                              <td>
                                <div className="proj-name">{sq.subject}</div>
                                <div className="proj-cat">{sq.quiz}</div>
                              </td>
                              <td className="num">{qCount} {qCount === 1 ? "Question" : "Questions"}</td>
                              <td className="num">{score !== null ? score : "—"}</td>
                              <td>
                                <span className="pill pill-success"><span className="dot" />Completed</span>
                              </td>
                            </tr>
                          );
                        })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : route.name === "quiz" && currentQuiz ? (
          <>
            <div className="page-head">
              <div>
                <h1><FaBook style={{ marginRight: 8, color: "var(--accent)" }} />Quiz · {currentQuiz.subject}</h1>
                <div className="page-desc">{currentQuiz.quiz}</div>
              </div>
              <button className="btn btn-ghost" onClick={gotoHome}><FaArrowLeft /> Back</button>
            </div>

            <div
              className="panel"
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "var(--sp-3) var(--sp-4)", marginBottom: "var(--sp-4)" }}
            >
              <div style={{ fontSize: 12.5, color: "var(--text-2)" }}>Answer the questions and submit.</div>
              <span className="chip">Questions: {currentQuiz.questions.length}</span>
            </div>

            {/* Quiz form */}
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setShowResults(true);
                const endTime = Date.now();
                setQuizEndTime(endTime);

                const t = quizStartTime ? Math.floor((endTime - quizStartTime) / 1000) : 0;
                const finalScore = calcScoreWithTime(currentQuiz.questions, userAnswers, t);

                let name = "";
                try { const u = JSON.parse(localStorage.getItem("user")); name = (u && u.name) ? u.name : ""; } catch {}

                try {
                  const r = await authFetch(`${API_BASE}/save_quiz_score.php`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ sliitid, name, score: finalScore, quizId: currentQuiz.quiz_id })
                  });
                  const d = await r.json();
                  if (d?.success) {
                    if (Array.isArray(d.completed_quiz_ids)) setCompletedIds(d.completed_quiz_ids);
                    if (d.quiz_scores && typeof d.quiz_scores === "object") setQuizScores(d.quiz_scores);
                    if (typeof d.total_score === "number") setTotalScore(d.total_score);
                  } else {
                    console.warn("Failed to save score:", d?.error);
                  }
                } catch (err) {
                  console.warn("Error saving score:", err);
                }
              }}
            >
              {currentQuiz.questions.map((q, idx) => {
                const correctIdx = parseInt(q.correct_option, 10) - 1;
                const chosen = userAnswers[q.id];
                return (
                  <div className="panel" style={{ padding: "var(--sp-4)", marginBottom: "var(--sp-3)" }} key={q.id}>
                    <div style={{ fontWeight: 700, marginBottom: "var(--sp-3)", fontSize: 13 }}>Q{idx + 1}. {q.question}</div>
                    <div style={{ display: "grid", gap: 8 }}>
                      {[q.option1, q.option2, q.option3, q.option4].map((opt, optIdx) => {
                        const active = chosen === optIdx;
                        const stateClass = showResults
                          ? (optIdx === correctIdx ? "is-correct" : (active ? "is-wrong" : ""))
                          : (active ? "is-active" : "");
                        return (
                          <label
                            key={optIdx}
                            className={`quiz-opt ${stateClass}`}
                            onClick={() => !showResults && setUserAnswers(prev => ({ ...prev, [q.id]: optIdx }))}
                          >
                            <span className="bullet" />
                            <input type="radio" name={`q${q.id}`} checked={active} onChange={() => {}} disabled={showResults} style={{ display: "none" }} />
                            <span style={{ flex: 1, fontSize: 13 }}>{opt}</span>
                            {showResults && optIdx === correctIdx && <FaCheckCircle color="var(--success)" />}
                            {showResults && active && optIdx !== correctIdx && <FaTimesCircle color="var(--error)" />}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {!showResults ? (
                <button type="submit" className="btn btn-primary" style={{ width: 220, marginTop: "var(--sp-2)" }}>
                  <FaAward /> Submit
                </button>
              ) : (
                <div className="panel" style={{ padding: "var(--sp-5)", marginTop: "var(--sp-4)", borderLeft: "3px solid var(--success)" }}>
                  {(() => {
                    const t = quizEndTime && quizStartTime ? Math.floor((quizEndTime - quizStartTime) / 1000) : 0;
                    const scoreWithTime = calcScoreWithTime(currentQuiz.questions, userAnswers, t);
                    const raw = calcScore(currentQuiz.questions, userAnswers);
                    return (
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                          <FaAward color="var(--success)" />
                          Final Score: <span className="num" style={{ color: "var(--success)" }}>{scoreWithTime} / {currentQuiz.questions.length * 10}</span>
                        </div>
                        <div className="num" style={{ color: "var(--text-2)", fontSize: 12 }}>
                          Raw: {raw} · Time: {t}s
                          {t > 30 && <span style={{ color: "var(--warning)", marginLeft: 8 }}>(Time Penalty)</span>}
                        </div>
                        <div style={{ marginTop: 14 }}>
                          <button className="btn btn-ghost" type="button" onClick={gotoHome}><FaArrowLeft /> Back to All Quizzes</button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </form>
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-icon"><FaTimesCircle /></div>
            <h3>Quiz not found</h3>
            <p>This quiz may have been removed, or the link you followed is incorrect.</p>
            <button className="btn btn-ghost" onClick={gotoHome}><FaArrowLeft /> Back to Education</button>
          </div>
        )}
      </main>
    </>
  );
}
